import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import * as THREE from "three";
import {
  createCammyActor,
  CAMMY_ACTIONS,
} from "../vendor/cammy-embed/cammy-actions.js";
import logoUrl from "../vendor/cammy-embed/camcom-logo.png";
import { SITE_MAP } from "../config/siteMap";
import { usePageSections } from "../context/PageSectionsContext";
import {
  useCammySpeechMessage,
  useCammyPageSummary,
} from "../context/CammySpeechContext";
import { hasSeenTour, PARKHI_INTRO_TOUR_KEY } from "../onboarding";
import { markTourSeen } from "../onboarding/tourStorage";

/**
 * Cammy, docked over the sidebar by default and user-draggable anywhere else,
 * driven by the embed kit in src/vendor/cammy-embed.
 *
 * The kit's own scaffolding (see its README) spans the whole viewport so Cammy
 * can roam the page. Here he's pinned to a small floating dock instead, using
 * the same orthographic pixels-per-metre setup measured against that dock.
 *
 * Two things that fall out of shrinking the kit's stage:
 *
 * 1. `snap` parks its car/house prop 1.7m from Cammy and `run` traverses the
 *    full bounds — neither fits in a 300px dock, so the stage widens while
 *    those play and shrinks back when he's idle. The camera is anchored to the
 *    dock's *right* edge rather than its centre, so the extra room opens to the
 *    left and Cammy doesn't slide when it resizes.
 * 2. The canvas never takes pointer events, so it can't block the page. A
 *    hotspot sits over Cammy for clicks, as the kit's README suggests.
 */

const PXM = 140; // pixels per metre — Cammy stands ~1.23m, peaks ~1.85m mid-flip
const DOCK_H = 292;
const DOCK_W_COMPACT = 300;
const DOCK_W_WIDE = 660; // room for the sedan prop at 1.7m plus its own length
const RIGHT_MARGIN_M = DOCK_W_COMPACT / 2 / PXM; // Cammy's distance from the right edge

// Matches the redesign sidebar's own w-[208px] (src/redesign/components/SideBar.js).
// Not read from there directly — it's a layout constant of a different
// component — but the two need to agree for "mid of the sidebar" to be true.
const SIDEBAR_WIDTH = 208;
// How much of the dock must stay reachable on screen once dragged — Cammy
// himself sits roughly centred in the compact box, so this keeps him (not
// just empty canvas) from ever being draggable fully out of view.
const DRAG_VISIBLE_MARGIN = 72;
const DRAG_THRESHOLD_PX = 4; // below this, a pointer-down+up is a click, not a drag

// The click menu's own box — kept separate from the dock's, since it has to
// clamp and flip to fit the viewport independently of where the dock sits.
const MENU_WIDTH = 264;
const MENU_GAP = 8;
const MENU_MIN_HEIGHT = 300; // below this much room, prefer the side with more

/**
 * Cammy starts roughly centred on the sidebar rather than tucked in the
 * viewport's bottom-right corner — the dock is still anchored by its right
 * edge (see the comment on RIGHT_MARGIN_M above), so this picks the `right`
 * distance that puts the box's centre, where Cammy stands, over the
 * sidebar's horizontal centre.
 *
 * Clamped so the whole box stays on screen: the speech bubble and the click
 * menu both render inside this same footprint (unlike the mostly-transparent
 * canvas, they have real content right at the edges), so letting the box
 * bleed past the left edge to hit the sidebar's literal centre clipped both
 * of them instead.
 */
const defaultDockPosition = () => {
  if (typeof window === "undefined") return { top: 12, right: 12 };
  const idealRight = window.innerWidth - SIDEBAR_WIDTH / 2 - DOCK_W_COMPACT / 2;
  return {
    top: Math.min(
      Math.max(window.innerHeight / 2 - DOCK_H / 2, 8),
      window.innerHeight - DOCK_H - 8,
    ),
    right: Math.max(Math.min(idealRight, window.innerWidth - DOCK_W_COMPACT - 8), 8),
  };
};
// Floor sits a little above the dock's bottom edge so neither his feet nor
// the car prop's wheels touch the crop. Flip still clears the top:
// -0.11 + 292/140 = 1.97m of headroom vs a 1.85m peak.
const GROUND_M = -0.11;
const ROAM_INSET_PX = 58; // keep him clear of the dock edges when running

// Actions that need more than the compact dock can show.
const WIDE_ACTIONS = new Set(["snap", "run"]);

// Where Cammy stands at rest: world x=0, which the camera places
// RIGHT_MARGIN_M in from the dock's right edge.
const HOME_X = 0;
// Past this he'd be left outside the compact dock, so walk him back. Must
// exceed the kit's own 0.15m "close enough" threshold in runTo(), otherwise
// the homing call becomes a no-op that fires every frame.
const HOME_EPSILON = 0.2;

const ACTION_LABELS = {
  run: "Take a run",
  flip: "Backflip",
  fact: "Tell me a fact",
  laptop: "Laptop time",
  snapCar: "Snap a car",
  snapHouse: "Snap a property",
  point: "Point at something",
};

// Shown once per login (PARKHI_INTRO_TOUR_KEY is re-armed by
// resetOnboardingForNewSession in onboarding/index.js, which only runs from
// an actual login — not from initializeAuth()'s session restore on refresh).
//
// No timer: it stays up across the pre-dashboard screens (Login.js's
// inspection-type picker, ModuleSelect.js), surviving each one's own
// unmounted-then-remounted CammyDock instance via PARKHI_INTRO_PENDING_KEY —
// a sessionStorage flag, since the local `bubble` state it drives can't
// itself survive a different component unmounting/remounting this one. It
// clears the moment any /redesign/* page actually mounts (see the two
// `PARKHI_INTRO_PENDING_KEY` effects below).
// Segments rather than one string: "Parakh"/"Parkhi" render bold+red, and the
// typing effect below needs to reveal characters across both plain and
// emphasised runs in one continuous sequence.
const PARKHI_INTRO_SEGMENTS = [
  { text: "Hi, welcome to " },
  { text: "Parakh", emphasis: true },
  { text: ". I'm " },
  { text: "Parkhi", emphasis: true },
  {
    text: " — let's take a closer look at every inspection together and uncover the details that matter.",
  },
];
const PARKHI_INTRO_LENGTH = PARKHI_INTRO_SEGMENTS.reduce(
  (n, seg) => n + seg.text.length,
  0,
);
const PARKHI_INTRO_PENDING_KEY = "parkhi_intro_pending";
// Separate from the pending key above: that one tracks "is the greeting
// still owed", this one tracks "has it already been typed out once" — once
// per browser tab (sessionStorage, not localStorage), so the handoff between
// Login.js's dock instance and the dashboard's own doesn't replay the
// animation on the second mount.
const PARKHI_INTRO_TYPED_KEY = "parkhi_intro_typed";
const PARKHI_INTRO_TYPE_MS = 28; // per character

/** `displayedBubble` is set to this, not the literal message, when the intro
 * is active — the bubble JSX below swaps in <ParkhiIntroText/> for it, since
 * the intro needs rich (bold/red) markup and a typing effect that every
 * other plain-string message doesn't. */
const PARKHI_INTRO_SENTINEL = "__parkhi_intro__";

/** Renders PARKHI_INTRO_SEGMENTS, revealed up to `count` characters total —
 * used both for the mid-type partial render and the final full one. */
function renderIntroSegments(count) {
  let remaining = count;
  return PARKHI_INTRO_SEGMENTS.map((seg, i) => {
    if (remaining <= 0) return null;
    const slice = seg.text.slice(0, remaining);
    remaining -= slice.length;
    return seg.emphasis ? (
      <strong key={i} className="font-bold text-red-600">
        {slice}
      </strong>
    ) : (
      <span key={i}>{slice}</span>
    );
  });
}

/**
 * The intro's own bubble content: typed out character-by-character the
 * first time (per tab), shown instantly in full on every render after that
 * — including a second CammyDock mount that inherits an already-showing
 * intro (see PARKHI_INTRO_TYPED_KEY above), and including a re-render of
 * *this* mount while typing (dragging, hovering, etc. don't restart it,
 * since React keeps this same component instance across those — only an
 * actual unmount/remount would, which is exactly the case the flag covers).
 */
function ParkhiIntroText() {
  const alreadyTyped = (() => {
    try {
      return sessionStorage.getItem(PARKHI_INTRO_TYPED_KEY) === "1";
    } catch {
      return true; // storage unavailable — fail toward showing full text
    }
  })();
  const [revealCount, setRevealCount] = useState(
    alreadyTyped ? PARKHI_INTRO_LENGTH : 0,
  );

  useEffect(() => {
    if (alreadyTyped) return;
    let count = 0;
    const timer = setInterval(() => {
      count += 1;
      setRevealCount(count);
      if (count >= PARKHI_INTRO_LENGTH) {
        clearInterval(timer);
        try {
          sessionStorage.setItem(PARKHI_INTRO_TYPED_KEY, "1");
        } catch {
          /* storage unavailable — animation still completes visually */
        }
      }
    }, PARKHI_INTRO_TYPE_MS);
    return () => clearInterval(timer);
    // alreadyTyped is read once at mount by design — see the function doc.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Larger than the standard 12px bubble text, scoped to just the intro —
  // every other message (row hover/click) keeps the original size.
  return (
    <span className="text-[14px] leading-[1.5]">
      {renderIntroSegments(revealCount)}
    </span>
  );
}

/**
 * The richer card shown in Parkhi's speech bubble while hovering him on a
 * page that published a pageSummary (currently only the case detail page) —
 * deliberately a different shape than the plain one-line text the Cases
 * list's row hover/click uses, so "a case's jist" reads as its own distinct
 * kind of moment rather than the same bubble with different words.
 */
function ParkhiCaseSummaryCard({ summary }) {
  return (
    <div className="flex flex-col gap-[6px]">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-[12.5px] font-semibold text-cl-text">
          {summary.title}
        </span>
        {summary.status && (
          <span
            className="flex-none rounded-pill px-[8px] py-[2px] text-[10px] font-semibold"
            style={{
              background: summary.statusChip?.bg,
              color: summary.statusChip?.fg,
            }}
          >
            {summary.status}
          </span>
        )}
      </div>
      {summary.facts?.length > 0 && (
        <div className="flex flex-col gap-[3px] border-t border-cl-head pt-[6px]">
          {summary.facts.map((fact) => (
            <div
              key={fact.label}
              className="flex items-center justify-between gap-2 text-[11px] text-cl-muted"
            >
              <span>{fact.label}</span>
              <span className="font-medium text-cl-secondary">
                {fact.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CammyDock({ autoPlay: autoPlayDefault = false, glow = false }) {
  const hostRef = useRef(null);
  const actorRef = useRef(null);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [bubble, setBubble] = useState("");
  const [flash, setFlash] = useState(0);
  const [wide, setWide] = useState(false);
  const [ready, setReady] = useState(false);
  const [pos, setPos] = useState(defaultDockPosition);
  const [dragging, setDragging] = useState(false);
  // Mutable scratch for the in-progress gesture — pointermove fires far too
  // often to route every sample through state before deciding "is this a
  // drag yet?".
  const dragRef = useRef({ active: false, moved: false, startX: 0, startY: 0, startTop: 0, startRight: 0 });
  // Off by default: unprompted backflips and a self-expanding overlay are a
  // distraction while someone is reviewing a claim. Tricks stay on demand.
  const [autoPlay, setAutoPlay] = useState(autoPlayDefault);
  // Read inside the setup effect, which must run exactly once — rebuilding the
  // WebGL scene because a prop changed would be wrong, so this is a ref rather
  // than a dependency. Flip it at runtime with the panel's toggle.
  const initialAutoPlay = useRef(autoPlayDefault);
  const location = useLocation();
  const pageSections = usePageSections();
  // A page (e.g. the Cases list, on hover/click) can make Cammy say something
  // of its own — takes priority over his own scripted bubble while active.
  const externalSpeech = useCammySpeechMessage();
  const displayedBubble = externalSpeech?.text || bubble;
  // Hovering Parkhi himself (not a row, not a click) surfaces whatever the
  // current page published as its own summary — the case detail page's own
  // case, currently. Independent of externalSpeech/bubble above; suppressed
  // while the click menu is open so the two never visually compete for the
  // same corner of the dock.
  const [hoveringAvatar, setHoveringAvatar] = useState(false);
  const pageSummary = useCammyPageSummary();
  const hoverSummary = hoveringAvatar && !open ? pageSummary : null;

  // Lean: an isolated, additive glance toward whatever the current message is
  // about (see setLean in cammy-actions.js — it can't disturb a trick/gesture
  // already in progress). No leanY on the message — true for every message
  // the dock's own tricks produce internally, since those never go through
  // this context — means no lean at all, so removing this effect (and the
  // leanY plumbing in CammySpeechContext) leaves everything else unaffected.
  useEffect(() => {
    const leanY = externalSpeech?.leanY;
    if (typeof leanY !== "number") {
      actorRef.current?.setLean(0);
      return;
    }
    const cammyCenterY = pos.top + DOCK_H / 2;
    actorRef.current?.setLean(leanY < cammyCenterY ? -1 : 1);
  }, [externalSpeech, pos.top]);

  useEffect(() => setOpen(false), [location.pathname]);

  // Companion to the mount-time check above, for the one path it can't
  // cover: a dock that was ALREADY mounted on some /redesign/* page (so its
  // mount-time check already ran and found the flag not yet set to clear)
  // and only afterwards navigates to another /redesign/* page in-app, same
  // instance, no remount. Login.js → Layout.js is a route change, so that
  // handoff unmounts one dock and mounts another — the new one's own
  // mount-time check (pathname already /redesign/cases) is what clears the
  // flag there, before this effect even runs once on it.
  useEffect(() => {
    if (!location.pathname.startsWith("/redesign/")) return;
    if (sessionStorage.getItem(PARKHI_INTRO_PENDING_KEY) !== "1") return;
    sessionStorage.removeItem(PARKHI_INTRO_PENDING_KEY);
    setBubble("");
  }, [location.pathname]);

  useEffect(() => {
    const host = hostRef.current;
    let disposed = false;
    let bubbleTimer = null;

    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearAlpha(0);
    renderer.domElement.style.cssText = `display:block;width:100%;height:100%;${
      // His head and limbs are near-black — invisible against a dark login
      // background without this. Two stacked drop-shadows (tight + wide) read
      // as one soft halo rather than a single hard-edged outline; harmless on
      // the light dashboard, so only opted into where the caller needs it.
      glow
        ? "filter:drop-shadow(0 0 10px rgba(255,255,255,0.75)) drop-shadow(0 0 28px rgba(255,255,255,0.45));"
        : ""
    }`;
    host.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.add(new THREE.HemisphereLight(0xffffff, 0x99a0aa, 1.25));
    const dir = new THREE.DirectionalLight(0xffffff, 1.5);
    dir.position.set(2, 5, 4);
    scene.add(dir);

    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 50);
    camera.position.z = 12;

    // Named rather than inlined as the actor's onSay option: the one-time
    // intro below needs the exact same bubble/timer behaviour without going
    // through the action system (it isn't a gesture, just a greeting).
    // `secs` omitted (the intro's own call) means "leave it up" — every other
    // caller here is a scripted trick and always passes one.
    const speak = (text, secs) => {
      if (disposed) return;
      setBubble(text);
      window.clearTimeout(bubbleTimer);
      if (secs) {
        bubbleTimer = window.setTimeout(
          () => !disposed && setBubble(""),
          Math.max(1, secs) * 1000,
        );
      }
    };

    const actor = createCammyActor(THREE, scene, {
      logoUrl,
      autoPlay: initialAutoPlay.current && !reduceMotion,
      onSay: speak,
      onFlash: (value) => {
        if (!disposed) setFlash(value);
      },
    });
    actorRef.current = actor;

    // Bounds always describe the *wide* stage, even while the dock is compact.
    // `snap` decides which side of Cammy to park its prop when the action
    // starts, from the midpoint of the bounds — measure that against the
    // compact dock and, because Cammy stands at its right edge, the prop is
    // placed off-stage to the right. Fixing the bounds keeps the midpoint left
    // of him, so props and runs always head into the room that opens up.
    actor.setBounds(
      RIGHT_MARGIN_M - (DOCK_W_WIDE - ROAM_INSET_PX) / PXM,
      RIGHT_MARGIN_M - ROAM_INSET_PX / PXM,
    );

    // Re-fit whenever the dock's rendered width changes (CSS animates it, so
    // this runs for a few frames during the transition).
    let lastW = 0;
    let lastH = 0;
    const fit = () => {
      const w = host.clientWidth;
      const h = host.clientHeight;
      if (!w || !h || (w === lastW && h === lastH)) return;
      lastW = w;
      lastH = h;
      renderer.setSize(w, h, false);
      camera.right = RIGHT_MARGIN_M;
      camera.left = RIGHT_MARGIN_M - w / PXM;
      camera.bottom = GROUND_M;
      camera.top = GROUND_M + h / PXM;
      camera.updateProjectionMatrix();
    };
    fit();
    setReady(true);

    // First mount after a login arms the pending flag; every mount thereafter
    // (this one includes — a fresh page load can be the very mount that finds
    // it already set) re-shows the same greeting for as long as the flag
    // survives. It's cleared, not by a timer, but by the pathname effect
    // below the moment a /redesign/* page actually mounts — including this
    // same check, so a mount that lands directly on one never flashes it.
    if (!hasSeenTour(PARKHI_INTRO_TOUR_KEY)) {
      markTourSeen(PARKHI_INTRO_TOUR_KEY);
      sessionStorage.setItem(PARKHI_INTRO_PENDING_KEY, "1");
    }
    if (sessionStorage.getItem(PARKHI_INTRO_PENDING_KEY) === "1") {
      if (location.pathname.startsWith("/redesign/")) {
        sessionStorage.removeItem(PARKHI_INTRO_PENDING_KEY);
      } else {
        speak(PARKHI_INTRO_SENTINEL);
      }
    }

    let visible = true;
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
    });
    io.observe(host);

    // Widen for the actions that need room, then settle back once he's idle.
    let idleFor = 0;
    let lastWide = false;
    let prevMs = null;

    renderer.setAnimationLoop((ms) => {
      if (!visible) return;
      const dt = prevMs == null ? 0 : Math.min((ms - prevMs) / 1000, 0.1);
      prevMs = ms;
      fit();
      actor.update(ms / 1000);

      // `run` finishes at the far end of the wide stage. Once the stage
      // shrinks back that spot is off-canvas, so send him home first — the
      // return trip is itself a 'run', which holds the stage open until he
      // arrives. Covers auto-played runs as well as menu-triggered ones.
      let current = actor.currentAction();
      if (
        !current &&
        Math.abs(actor.cammy.position.x - HOME_X) > HOME_EPSILON
      ) {
        actor.runTo(HOME_X);
        current = actor.currentAction();
      }

      const needsRoom = !!current && WIDE_ACTIONS.has(current);
      if (needsRoom) {
        idleFor = 0;
        if (!lastWide) {
          lastWide = true;
          setWide(true);
        }
      } else if (lastWide) {
        // Hold the wide stage briefly so the prop can finish leaving.
        idleFor += dt;
        if (idleFor > 0.6) {
          lastWide = false;
          idleFor = 0;
          setWide(false);
        }
      }

      renderer.render(scene, camera);
    });

    return () => {
      disposed = true;
      window.clearTimeout(bubbleTimer);
      io.disconnect();
      renderer.setAnimationLoop(null);
      actorRef.current = null;
      // Everything in this scene was built by the kit for this mount alone, so
      // it is all ours to release.
      scene.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        const materials = Array.isArray(object.material)
          ? object.material
          : object.material
            ? [object.material]
            : [];
        materials.forEach((material) => {
          Object.values(material).forEach((value) => {
            if (value && value.isTexture) value.dispose();
          });
          material.dispose();
        });
      });
      renderer.dispose();
      host.innerHTML = "";
    };
    // location.pathname is deliberately not a dependency — it's read once
    // above for the intro's mount-time check, same "once at mount, on
    // purpose" spirit as initialAutoPlay; re-running this effect on every
    // navigation would tear down and rebuild the whole WebGL scene.
  }, [glow]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const play = useCallback((action) => {
    actorRef.current?.play(action);
    setOpen(false);
  }, []);

  /**
   * The hotspot over Cammy doubles as the drag handle: pointer-down arms a
   * potential drag, pointer-up either committed one (moved past the
   * threshold at some point) or, if the pointer never really moved, is just
   * the click that opens the menu — the same distinction CarDamage3D.jsx
   * makes between picking a panel and orbiting the camera.
   */
  const clampDockPos = useCallback(
    (top, right) => {
      const boxWidth = wide ? DOCK_W_WIDE : DOCK_W_COMPACT;
      const maxTop = window.innerHeight - DRAG_VISIBLE_MARGIN;
      const minTop = -(DOCK_H - DRAG_VISIBLE_MARGIN);
      const maxRight = window.innerWidth - DRAG_VISIBLE_MARGIN;
      const minRight = -(boxWidth - DRAG_VISIBLE_MARGIN);
      return {
        top: Math.min(maxTop, Math.max(minTop, top)),
        right: Math.min(maxRight, Math.max(minRight, right)),
      };
    },
    [wide],
  );

  const onHandlePointerDown = useCallback(
    (event) => {
      if (event.button != null && event.button !== 0) return;
      dragRef.current = {
        active: true,
        moved: false,
        startX: event.clientX,
        startY: event.clientY,
        startTop: pos.top,
        startRight: pos.right,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [pos],
  );

  const onHandlePointerMove = useCallback(
    (event) => {
      const drag = dragRef.current;
      if (!drag.active) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      if (!drag.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
        drag.moved = true;
        setDragging(true);
        // Pointer capture (set on pointerdown, for move/up to keep routing
        // here past the button's own bounds) doesn't reliably suppress
        // pointerleave across browsers, so the hover-triggered summary card
        // could otherwise stay stuck up for the whole drag. Belt and
        // suspenders: clear it the instant a drag is recognised.
        setHoveringAvatar(false);
      }
      if (!drag.moved) return;
      setPos(clampDockPos(drag.startTop + dy, drag.startRight - dx));
    },
    [clampDockPos],
  );

  const onHandlePointerUp = useCallback((event) => {
    const drag = dragRef.current;
    drag.active = false;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (drag.moved) {
      setDragging(false);
    } else {
      setOpen((value) => !value);
    }
  }, []);

  // Where the click menu renders, computed fresh on every render — `open`
  // toggling true already re-renders this, so the values are current
  // whenever they matter. Positioned in viewport space (not relative to the
  // dock box) and clamped/flipped to fit: the dock can sit mid-screen now
  // instead of pinned to the bottom-right corner, where "open upward" always
  // had the whole viewport above it to grow into. Mid-screen, it often
  // doesn't, and the menu was clipping against the top of the viewport.
  const boxRight = window.innerWidth - pos.right;
  const spaceAbove = pos.top;
  const spaceBelow = window.innerHeight - pos.top - DOCK_H;
  const menuOpensBelow = spaceAbove < MENU_MIN_HEIGHT && spaceBelow > spaceAbove;
  const menuLeft = Math.min(
    Math.max(boxRight - MENU_WIDTH, MENU_GAP),
    window.innerWidth - MENU_WIDTH - MENU_GAP,
  );
  const menuMaxHeight = Math.max(
    200,
    (menuOpensBelow ? spaceBelow : spaceAbove) - MENU_GAP * 2,
  );

  return (
    // z-fixed (30) keeps the dock above page content but below every modal
    // layer (40+). At z-popover he floated over the edit/parts/location
    // modals, and his click hotspot could land on their buttons.
    <div
      ref={wrapRef}
      className="pointer-events-none fixed z-fixed font-clearline"
      style={{
        top: pos.top,
        right: pos.right,
        width: wide ? DOCK_W_WIDE : DOCK_W_COMPACT,
        height: DOCK_H,
        transition: "width 280ms cubic-bezier(0.22, 0.61, 0.36, 1)",
      }}
    >
      {/* Speech bubble, held over Cammy rather than the widened stage. A
          hover-triggered page summary renders the richer card instead of
          plain text — everything else here (position, sizing, chrome) stays
          the same bubble either way.

          Positioned in viewport space and clamped to the top edge, same
          reasoning as the click menu below: dragging the dock near the top
          of the screen is a position DRAG_VISIBLE_MARGIN explicitly allows
          (it only promises the avatar itself stays reachable, not the whole
          292px box), and the box's own top can end up well above the
          viewport — where this bubble used to render, unreachable, while the
          avatar near the box's bottom stayed visible right below it. */}
      {(displayedBubble || hoverSummary) && (
        <div
          role="status"
          className="pointer-events-none fixed rounded-card border border-cl-card bg-cl-surface px-3 py-2 text-[12px] leading-[1.45] text-cl-text shadow-[0_8px_24px_rgba(19,23,32,0.14)]"
          style={{
            top: Math.max(MENU_GAP, pos.top),
            right: pos.right + 8,
            width: DOCK_W_COMPACT - 16,
          }}
        >
          {displayedBubble ? (
            displayedBubble === PARKHI_INTRO_SENTINEL ? (
              <ParkhiIntroText />
            ) : (
              displayedBubble
            )
          ) : (
            <ParkhiCaseSummaryCard summary={hoverSummary} />
          )}
        </div>
      )}

      {/* Camera-flash bloom, kept inside the dock rather than over the page */}
      {flash > 0 && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-card bg-white"
          style={{ opacity: Math.min(0.55, flash) }}
        />
      )}

      <div ref={hostRef} className="absolute inset-0" aria-hidden="true" />

      {/* Hotspot over Cammy — the canvas stays click-through. Doubles as the
          drag handle (a plain click, no real movement, still opens the menu)
          and as the hover trigger for the current page's summary card.

          onKeyDown handles Enter/Space explicitly rather than relying on
          onClick: the drag gesture is built entirely from pointer events
          (see onHandlePointerUp), and a plain onClick here would double-fire
          for a mouse click — once from onHandlePointerUp's own click
          detection, once from the browser's native post-click event on this
          real <button>. Keyboard activation never goes through the pointer
          handlers at all, so toggling here directly is the only path for it
          and can't double-fire anything. */}
      <button
        ref={buttonRef}
        type="button"
        onPointerDown={onHandlePointerDown}
        onPointerMove={onHandlePointerMove}
        onPointerUp={onHandlePointerUp}
        onPointerCancel={onHandlePointerUp}
        onPointerEnter={() => setHoveringAvatar(true)}
        onPointerLeave={() => setHoveringAvatar(false)}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          setOpen((value) => !value);
        }}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Parkhi — sitemap and tricks. Draggable to reposition."
        title="Ask Parkhi — drag to move"
        disabled={!ready}
        className="pointer-events-auto absolute bottom-2 touch-none rounded-card focus:outline-none focus-visible:ring-2 focus-visible:ring-cl-accent"
        style={{
          right: DOCK_W_COMPACT / 2 - 55,
          width: 110,
          height: 150,
          cursor: dragging ? "grabbing" : "grab",
        }}
      />

      {open && (
        <div
          role="menu"
          aria-label="Parkhi"
          className="pointer-events-auto fixed overflow-y-auto overflow-x-hidden rounded-card border border-cl-card bg-cl-surface shadow-[0_12px_32px_rgba(19,23,32,0.18)]"
          style={{
            left: menuLeft,
            width: MENU_WIDTH,
            maxHeight: menuMaxHeight,
            top: menuOpensBelow ? pos.top + DOCK_H + MENU_GAP : undefined,
            bottom: menuOpensBelow
              ? undefined
              : window.innerHeight - pos.top + MENU_GAP,
          }}
        >
          <div className="border-b border-cl-head px-4 py-[10px]">
            <div className="text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label">
              Parkhi
            </div>
            <div className="mt-[2px] text-[12px] text-cl-muted">
              {pageSections.length
                ? "Jump to a section or page, or ask for a trick"
                : "Jump to a page, or ask for a trick"}
            </div>
          </div>

          <div className="cl-scroll max-h-[52vh] overflow-y-auto py-1">
            {pageSections.length > 0 && (
              <div className="px-1 py-1">
                <div className="px-3 py-1 text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label">
                  On this page
                </div>
                {pageSections.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      section.go();
                      setOpen(false);
                    }}
                    className="flex w-full items-baseline justify-between gap-2 rounded-tile px-3 py-[6px] text-left transition-colors hover:bg-cl-hover"
                  >
                    <span className="text-[12.5px] font-semibold text-cl-text">
                      {section.label}
                    </span>
                    {section.hint != null && (
                      <span className="flex-none text-[11px] text-cl-faint">
                        {section.hint}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {SITE_MAP.map((section) => (
              <div key={section.title} className="px-1 py-1">
                <div className="px-3 py-1 text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label">
                  {section.title}
                </div>
                {section.links.map((link) => {
                  const isCurrent =
                    link.path &&
                    (link.path === "/"
                      ? location.pathname === "/"
                      : location.pathname.startsWith(link.path));

                  if (link.disabled) {
                    return (
                      <div
                        key={link.id}
                        aria-disabled="true"
                        className="flex cursor-not-allowed items-baseline justify-between gap-2 rounded-tile px-3 py-[6px]"
                      >
                        <span className="text-[12.5px] font-medium text-cl-faint">
                          {link.label}
                        </span>
                        <span className="text-[9px] font-semibold tracking-[0.06em] text-cl-disabled">
                          SOON
                        </span>
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={link.id}
                      to={link.path}
                      role="menuitem"
                      onClick={() => setOpen(false)}
                      aria-current={isCurrent ? "page" : undefined}
                      className={`flex items-baseline justify-between gap-2 rounded-tile px-3 py-[6px] transition-colors ${
                        isCurrent ? "bg-cl-tint" : "hover:bg-cl-hover"
                      }`}
                    >
                      <span
                        className={`text-[12.5px] font-semibold ${
                          isCurrent ? "text-cl-accent" : "text-cl-text"
                        }`}
                      >
                        {link.label}
                      </span>
                      {isCurrent && (
                        <span className="text-[10px] font-semibold text-cl-accent">
                          You're here
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            ))}

            <div className="mt-1 border-t border-cl-head px-1 pb-1 pt-2">
              <div className="px-3 py-1 text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label">
                Tricks
              </div>
              {CAMMY_ACTIONS.map((action) => (
                <button
                  key={action}
                  type="button"
                  role="menuitem"
                  onClick={() => play(action)}
                  className="flex w-full items-center rounded-tile px-3 py-[6px] text-left text-[12.5px] font-medium text-cl-secondary transition-colors hover:bg-cl-hover hover:text-cl-accent"
                >
                  {ACTION_LABELS[action] || action}
                </button>
              ))}

              <button
                type="button"
                role="menuitemcheckbox"
                aria-checked={autoPlay}
                onClick={() => {
                  const next = !autoPlay;
                  setAutoPlay(next);
                  actorRef.current?.setAutoPlay(next);
                }}
                className="mt-1 flex w-full items-center justify-between gap-2 rounded-tile px-3 py-[6px] text-left transition-colors hover:bg-cl-hover"
              >
                <span className="text-[12.5px] font-medium text-cl-secondary">
                  Improvise when idle
                </span>
                <span
                  className={`flex-none rounded-pill px-[7px] py-px text-[10px] font-semibold ${
                    autoPlay
                      ? "bg-cl-accent text-white"
                      : "bg-cl-bg text-cl-muted"
                  }`}
                >
                  {autoPlay ? "ON" : "OFF"}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
